<template>
  <section class="dealer-banner">
    <div class="dealer-banner__background">
      <!-- <pre>{{ backgroundImage }}
      </pre> -->
      <picture
        v-if="backgroundImage"
        class="dealer-banner__bg-picture">
        <source
          v-if="backgroundImageAvif"
          type="image/avif"
          sizes="100vw"
          :srcset="backgroundImageAvif" />
        <source
          v-if="backgroundImageWebp"
          type="image/webp"
          sizes="100vw"
          :srcset="backgroundImageWebp" />
        <img
          :src="backgroundImage"
          class="dealer-banner__bg-image"
          alt=""
          loading="eager"
          fetchpriority="high"
          decoding="sync" />
      </picture>

      <div
        v-else
        class="dealer-banner__bg-fallback-image" />
    </div>

    <div class="dealer-banner__overlay"></div>

    <div class="dealer-banner__container">
      <div class="dealer-banner__content">
        <!-- Pre-title -->
        <div
          v-if="preTitle"
          class="dealer-banner__pre-title">
          {{ preTitle }}
        </div>

        <!-- Title -->
        <h1
          class="dealer-banner__title"
          v-html="title" />

        <!-- Sub-title -->
        <div
          v-if="subTitle"
          class="dealer-banner__sub-title">
          {{ subTitle }}
        </div>

        <!-- CTA -->
        <div
          v-if="showCta"
          class="dealer-banner__cta">
          <cta
            :text="ctaText"
            variant="solid"
            color="red"
            :hovered="true"
            :show-icon="ctaShowIcon"
            :animated-icon="ctaAnimatedIcon"
            @click="$emit('cta-click')" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
  import Cta from '../Cta.vue'

  // Props
  defineProps({
    backgroundImage: {
      type: [ Object, String ],
      required: true
    },
    backgroundImageWebp: {
      type: [ Object, String ],
      default: null
    },
    backgroundImageAvif: {
      type: [ Object, String ],
      default: null
    },
    preTitle: {
      type: [ String, null ],
      default: null
    },
    subTitle: {
      type: [ String, null ],
      default: null
    },
    title: {
      type: String,
      required: true,
      validator: value => typeof value === 'string' && value.trim().length > 0
    },
    showCta: {
      type: Boolean,
      default: true
    },
    ctaText: {
      type: String,
      default() {
        return useI18n().t('dealer.defaultTexts.discoverMore')
      }
    },
    ctaVariant: {
      type: String,
      default: 'solid',
      validator: value => [ 'solid', 'text' ].includes(value)
    },
    ctaShowIcon: {
      type: Boolean,
      default: false
    },
    ctaAnimatedIcon: {
      type: Boolean,
      default: false
    }
  })

  // Emits
  defineEmits([ 'cta-click' ])
</script>

<style lang="scss" scoped>
.dealer-banner {
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  // Background image
  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  &__bg-picture {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__bg-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    filter: brightness(50%);
  }

  &__bg-fallback-image {
    width: 100%;
    height: 100%;
    background-color: var(--color-red);
    filter: brightness(75%);
  }

  // Dark overlay for better text readability
  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgb(0, 0, 0, 0.3);
    z-index: 2;
  }

  // Content container
  &__container {
    position: relative;
    z-index: 3;
    width: 100%;

    // max-width: var(--grid-max-width);
    // margin: 0 auto;
    padding: 0 var(--grid-margin);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-height: 100vh;

    @include mq($until: tablet) {
      padding: 0 var(--space-s);
      min-height: 600px;
    }
  }

  // Content wrapper
  &__content {
    text-align: left;
    color: var(--color-white);
    width: 100%;
    display: flex;
    gap: var(--space-s);
    flex-direction: column;
    max-width: 800px;
  }

  // Pre-title styling
  &__pre-title {
    color: var(--color-white);
    font-family: var(--font-family-poppins);
    font-size: var(--typography-size-m); // 20px desktop, 18px mobile
    font-style: normal;
    font-weight: var(--fot-weight-medium);
    letter-spacing: 0.4px;

    @include mq($until: tablet) {
      font-size: var(--typography-size-m-mobile);
    }
  }

  // Sub-title styling (stessa grafica del pre-title, sotto il titolo)
  &__sub-title {
    color: var(--color-white);
    font-family: var(--font-family-poppins);
    font-size: var(--typography-size-m); // 20px desktop, 18px mobile
    font-style: normal;
    font-weight: var(--font-weight-medium);
    letter-spacing: 0.4px;

    @include mq($until: tablet) {
      font-size: var(--typography-size-m-mobile);
    }
  }

  // Title styling
  &__title {
    color: var(--color-white);
    font-family: var(--font-family-poppins);
    font-size: var(--typography-size-xl, 60px); // 60px desktop, 36px mobile
    font-style: normal;
    font-weight: var(--font-weight-medium);
    line-height: 120%;
    text-transform: uppercase;
  }

  // CTA container
  &__cta {
    display: flex;
    justify-content: flex-start;
  }

  // Responsive adjustments
  @include mq($until: tablet) {
    min-height: 80vh;
    height: auto;
  }

  @include mq($until: mobile) {
    min-height: 70vh;
  }
}
</style>
